import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { lightPalette } from "../../theme-layout/palette";

const ForecastHoursDetails = ({ open, onClose, scheduleData }) => {
  if (!scheduleData || scheduleData.length === 0) {
    return null;
  }

  const dayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const sortedScheduleDataDayWise = Array.isArray(scheduleData)
    ? [...scheduleData].sort(
        (firstSchedule, secondSchedule) =>
          dayOrder.indexOf(firstSchedule.day) -
          dayOrder.indexOf(secondSchedule.day),
      )
    : [];

  const groupedScheduleData = sortedScheduleDataDayWise.reduce(
    (groups, scheduleEntry) => {
      const previousGroup = groups[groups.length - 1];
      if (previousGroup?.day === scheduleEntry.day) {
        previousGroup.periods.push(scheduleEntry);
      } else {
        groups.push({ day: scheduleEntry.day, periods: [scheduleEntry] });
      }
      return groups;
    },
    [],
  );

  const totalForecastHours = sortedScheduleDataDayWise.reduce(
    (accumulatedHours, scheduleEntry) =>
      accumulatedHours + Number(scheduleEntry.totalDurationHour || 0),
    0,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${lightPalette.primary.lighter}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: 20,
          fontWeight: 500,
        }}
      >
        Schedule Details
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close schedule details"
        >
          <GridCloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: 1.5,
            borderColor: lightPalette.primary.lighter,
          }}
        >
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: lightPalette.primary.lightest2,
                  "& th": {
                    borderBottom: `1px solid ${lightPalette.primary.lighter}`,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    py: 1.5,
                  },
                }}
              >
                <TableCell sx={{ width: "24%" }}>Day</TableCell>
                <TableCell align="center" sx={{ width: "15%" }}>
                  Time Period
                </TableCell>
                <TableCell sx={{ width: "21%" }}>Start Time</TableCell>
                <TableCell sx={{ width: "21%" }}>End Time</TableCell>
                <TableCell align="center" sx={{ width: "19%" }}>
                  Total Hours
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedScheduleData.map((group) =>
                group.periods.map((row, periodIndex) => {
                  return (
                    <TableRow
                      key={`${group.day}-${periodIndex}`}
                      sx={{
                        backgroundColor: "lightPalette.common.white",
                        "& td": {
                          borderBottom: `1px solid ${lightPalette.primary.lighter}`,
                        },
                      }}
                    >
                      {periodIndex === 0 && (
                        <TableCell
                          rowSpan={group.periods.length}
                          sx={{
                            verticalAlign: "middle",
                            backgroundColor: "lightPalette.common.white",
                            "& td": {
                              borderBottom: `1px solid ${lightPalette.primary.lighter}`,
                            },
                          }}
                        >
                          {group.day}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            minWidth: 40,
                            justifyContent: "center",
                            px: 1,
                            py: 0.25,
                            fontSize: 13,
                          }}
                        >
                          {periodIndex + 1}
                        </Box>
                      </TableCell>
                      <TableCell>{row.startTime}</TableCell>
                      <TableCell>{row.endTime}</TableCell>
                      <TableCell align="center">
                        {Number(row.totalDurationHour || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  );
                }),
              )}
              <TableRow
                sx={{
                  backgroundColor: "lightPalette.common.white",
                  "& td": {
                    borderTop: `2px solid ${lightPalette.primary.main}`,
                    borderBottom: 0,
                    py: 1.5,
                    fontWeight: 700,
                  },
                }}
              >
                <TableCell colSpan={4}>Total Forecast Hours</TableCell>
                <TableCell align="center">
                  {totalForecastHours.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default ForecastHoursDetails;
